"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";

import type { AssignedProject, Site, Standard } from "@/types";

import type { FilterAssetOption } from "../components/filterScopeFields";
import { useFilterScopeData } from "../queries/useFilterScopeData";

export interface FilterScopeDraftFields {
  selectedProject: string;
  selectedProjectLabel: string;
  selectedSite: string;
  selectedSiteLabel: string;
  selectedAssets: string[];
  selectedAssetLabels: string[];
  selectedStandard: string;
}

interface UseFilterScopeCascadeOptions {
  role: string;
  draft: FilterScopeDraftFields;
  setDraft: (fields: Partial<FilterScopeDraftFields>) => void;
}

interface UseFilterScopeCascadeResult {
  standardsData: Standard[] | undefined;
  assignedProjectsData: AssignedProject[] | undefined;
  siteOptions: Site[];
  assetOptions: FilterAssetOption[];
  isProjectSelected: boolean;
  handleProjectChange: (projectId: string) => void;
  handleSiteChange: (siteId: string) => void;
  handleAssetsChange: (ids: string[]) => void;
  resetAutoSelection: () => void;
}

export function useFilterScopeCascade({
  role,
  draft,
  setDraft,
}: UseFilterScopeCascadeOptions): UseFilterScopeCascadeResult {
  const shouldAutoSelectSite = useRef(false);
  const shouldAutoSelectAssets = useRef(false);

  const { standardsData, assignedProjectsData, sitesData, assetsData } = useFilterScopeData({
    role,
    selectedProject: draft.selectedProject,
    selectedSite: draft.selectedSite,
  });

  const siteOptions = useMemo(
    () =>
      sitesData.some((site) => site.id === draft.selectedSite) || !draft.selectedSite
        ? sitesData
        : [{ id: draft.selectedSite, name: draft.selectedSiteLabel }, ...sitesData],
    [draft.selectedSite, draft.selectedSiteLabel, sitesData]
  );

  const assetOptions = useMemo<FilterAssetOption[]>(() => {
    const loaded = assetsData.map((asset) => ({ value: asset.id, label: asset.name }));
    const loadedIds = new Set(assetsData.map((asset) => asset.id));
    const labelMap = Object.fromEntries(
      draft.selectedAssets.map((id, index) => [id, draft.selectedAssetLabels[index] ?? id])
    );
    const fallback = draft.selectedAssets
      .filter((id) => !loadedIds.has(id))
      .map((id) => ({ value: id, label: labelMap[id] }));

    return [...fallback, ...loaded];
  }, [assetsData, draft.selectedAssetLabels, draft.selectedAssets]);

  useEffect(() => {
    if (shouldAutoSelectSite.current && sitesData.length > 0) {
      shouldAutoSelectSite.current = false;
      const first = sitesData[0];
      setDraft({ selectedSite: first.id, selectedSiteLabel: first.name });
    }
  }, [setDraft, sitesData]);

  useEffect(() => {
    if (shouldAutoSelectAssets.current && assetsData.length > 0) {
      shouldAutoSelectAssets.current = false;
      setDraft({
        selectedAssets: assetsData.map((asset) => asset.id),
        selectedAssetLabels: assetsData.map((asset) => asset.name),
      });
    }
  }, [assetsData, setDraft]);

  const handleProjectChange = useCallback(
    (projectId: string) => {
      shouldAutoSelectSite.current = true;
      shouldAutoSelectAssets.current = true;
      const project = assignedProjectsData?.find((item) => item.id === projectId);
      setDraft({
        selectedProject: projectId,
        selectedProjectLabel: project?.code ?? project?.name ?? "",
        selectedSite: "",
        selectedSiteLabel: "",
        selectedAssets: [],
        selectedAssetLabels: [],
      });
    },
    [assignedProjectsData, setDraft]
  );

  const handleSiteChange = useCallback(
    (siteId: string) => {
      shouldAutoSelectAssets.current = true;
      const site = siteOptions.find((item) => item.id === siteId);
      setDraft({
        selectedSite: siteId,
        selectedSiteLabel: site?.name ?? "",
        selectedAssets: [],
        selectedAssetLabels: [],
      });
    },
    [setDraft, siteOptions]
  );

  const handleAssetsChange = useCallback(
    (ids: string[]) => {
      const existingLabelMap = Object.fromEntries(
        draft.selectedAssets.map((id, index) => [id, draft.selectedAssetLabels[index]])
      );
      const labels = ids.map(
        (id) => assetsData.find((asset) => asset.id === id)?.name ?? existingLabelMap[id] ?? ""
      );
      setDraft({ selectedAssets: ids, selectedAssetLabels: labels });
    },
    [assetsData, draft.selectedAssetLabels, draft.selectedAssets, setDraft]
  );

  const resetAutoSelection = useCallback(() => {
    shouldAutoSelectSite.current = false;
    shouldAutoSelectAssets.current = false;
  }, []);

  return {
    assetOptions,
    assignedProjectsData,
    handleAssetsChange,
    handleProjectChange,
    handleSiteChange,
    isProjectSelected: Boolean(draft.selectedProject),
    resetAutoSelection,
    siteOptions,
    standardsData,
  };
}
